import { canLink } from "./canLink.js";

export type EditorKind = "start" | "case" | "rest" | "event" | "relic";

export type EditorNode = {
  id: string;
  kind: EditorKind;
  x: number;
  y: number;
  next: string[];
  size: "small" | "medium" | "large" | "boss";
  name: string;
  required: number;
  deadline: number;
  loads: { id: string; weight: number }[];
  flavor: string;
};

export type MapEdit = {
  nodes: Record<string, EditorNode>;
  selectedId: string | null;
  placeKind: EditorKind;
  nextSeq: number;
  cols: number;
  rows: number;
};

export function createMapEdit(): MapEdit {
  return { nodes: {}, selectedId: null, placeKind: "case", nextSeq: 1, cols: 7, rows: 8 };
}

export function addRow(state: MapEdit): MapEdit {
  return { ...state, rows: state.rows + 1 };
}

export function setPlaceKind(state: MapEdit, placeKind: EditorKind): MapEdit {
  return { ...state, placeKind };
}

function at(state: MapEdit, x: number, y: number): EditorNode | undefined {
  return Object.values(state.nodes).find((node) => node.x === x && node.y === y);
}

function place(state: MapEdit, x: number, y: number): MapEdit {
  if (state.placeKind === "start" && Object.values(state.nodes).some((n) => n.kind === "start")) {
    return state;
  }
  const id = `n${state.nextSeq}`;
  const node: EditorNode = {
    id,
    kind: state.placeKind,
    x,
    y,
    next: [],
    size: "small",
    name: "",
    required: 10,
    deadline: 4,
    loads: [],
    flavor: "",
  };
  return {
    ...state,
    nodes: { ...state.nodes, [id]: node },
    selectedId: null,
    nextSeq: state.nextSeq + 1,
  };
}

function link(state: MapEdit, fromId: string, toId: string): MapEdit {
  const from = state.nodes[fromId];
  if (!from || from.next.includes(toId)) return { ...state, selectedId: toId };
  return {
    ...state,
    selectedId: toId,
    nodes: { ...state.nodes, [fromId]: { ...from, next: [...from.next, toId] } },
  };
}

export function dropKind(state: MapEdit, kind: EditorKind, x: number, y: number): MapEdit {
  if (at(state, x, y)) return state;
  return place({ ...state, placeKind: kind, selectedId: null }, x, y);
}

export function moveNode(state: MapEdit, fromId: string, x: number, y: number): MapEdit {
  const from = state.nodes[fromId];
  if (!from || at(state, x, y)) return state;
  return { ...state, selectedId: null, nodes: { ...state.nodes, [fromId]: { ...from, x, y } } };
}

export function linkNode(state: MapEdit, fromId: string, x: number, y: number): MapEdit {
  const from = state.nodes[fromId];
  const hit = at(state, x, y);
  if (!from || !hit || !canLink(from, hit)) return state;
  return { ...link(state, fromId, hit.id), selectedId: null };
}

export function tapCell(state: MapEdit, x: number, y: number): MapEdit {
  const hit = at(state, x, y);
  if (!hit) return place(state, x, y);
  if (state.selectedId === hit.id) return { ...state, selectedId: null };
  return { ...state, selectedId: hit.id };
}

export function deleteNode(state: MapEdit, id: string): MapEdit {
  return deleteSelected({ ...state, selectedId: id });
}

export function deleteSelected(state: MapEdit): MapEdit {
  const id = state.selectedId;
  if (!id) return state;
  const nodes = Object.fromEntries(
    Object.entries(state.nodes)
      .filter(([key]) => key !== id)
      .map(([key, node]) => [key, { ...node, next: node.next.filter((n) => n !== id) }]),
  );
  return { ...state, nodes, selectedId: null };
}

export function unlinkSelected(state: MapEdit): MapEdit {
  const id = state.selectedId;
  if (!id || !state.nodes[id]) return state;
  return { ...state, nodes: { ...state.nodes, [id]: { ...state.nodes[id], next: [] } } };
}

export function updateSelected(state: MapEdit, patch: Partial<EditorNode>): MapEdit {
  const id = state.selectedId;
  if (!id || !state.nodes[id]) return state;
  return { ...state, nodes: { ...state.nodes, [id]: { ...state.nodes[id], ...patch, id } } };
}

export function editHint(_state: MapEdit): string {
  return "アイコンは移動。枠から矢印。下に行を足せる。捨てるで削除。";
}
